import { Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/utils/cn';

/**
 * What a church sees where a plan-gated capability would be.
 *
 * It says what the capability does and which plan includes it. A bare "upgrade
 * required" tells a pastor they cannot do something and nothing about whether it is
 * worth paying for, which is a bad experience and a wasted conversation.
 */

export interface PlanUpgradePromptProps {
  /** "Automated follow-up" — the capability, named the way the pricing page names it. */
  title: string;
  /** One or two sentences on what it does for the church. */
  description: string;
  /** "Included in Professional" — the caller resolves the plan name. */
  planNote: string;
  action?: { label: string; onClick: () => void };
  icon?: ReactNode;
  /** Inline sits within a page section; full replaces the page body. */
  variant?: 'inline' | 'full';
  className?: string;
}

export const PlanUpgradePrompt = ({
  title,
  description,
  planNote,
  action,
  icon = <Sparkles className="h-5 w-5" />,
  variant = 'inline',
  className,
}: PlanUpgradePromptProps) => (
  <Card
    className={cn(
      'flex flex-col items-center gap-3 border-dashed p-6 text-center',
      variant === 'full' && 'min-h-[20rem] justify-center',
      className,
    )}
  >
    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand/10 text-brand">
      {icon}
    </span>

    <div>
      <h3 className="text-card-title font-semibold text-fg">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-body-sm text-fg-secondary">{description}</p>
      <p className="mt-2 text-caption font-medium text-brand">{planNote}</p>
    </div>

    {action ? (
      <Button onClick={action.onClick} className="mt-1">
        {action.label}
      </Button>
    ) : null}
  </Card>
);
